import type { GeneratedTemplate } from '../../api/client';

interface TemplatePreviewCardProps {
  template: GeneratedTemplate;
  onEditZpl?: (templateKey: string, zpl: string) => void;
}

export function TemplatePreviewCard({ template, onEditZpl }: TemplatePreviewCardProps) {
  return (
    <div className="border rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-900">{template.templateName}</h4>
          <p className="text-xs text-gray-500">
            {template.labelType} — {template.dimensions.width}" × {template.dimensions.height}" @ {template.dpi} DPI
          </p>
        </div>
        <div className="flex items-center gap-2">
          {template.validationResult.staticValid ? (
            <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Valid ZPL</span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
              {template.validationResult.staticErrors.length} error(s)
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x">
        {/* ZPL Content */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="text-xs font-semibold text-gray-500 uppercase">ZPL Content</h5>
            {onEditZpl && (
              <button
                onClick={() => onEditZpl(template.templateKey, template.zplContent)}
                className="text-xs text-primary-600 hover:text-primary-800"
              >
                Edit
              </button>
            )}
          </div>
          <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded overflow-auto max-h-64 font-mono leading-relaxed">
            {template.zplContent}
          </pre>
        </div>

        {/* Preview / Metadata */}
        <div className="p-4 space-y-3">
          {/* Partials used */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Partials Used</h5>
            <div className="flex flex-wrap gap-1">
              {template.partialsUsed.map((p) => (
                <span key={p} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200">
                  {p}
                </span>
              ))}
              {template.partialsUsed.length === 0 && (
                <span className="text-xs text-gray-400">None</span>
              )}
            </div>
          </div>

          {/* Gaps */}
          {template.partialGaps.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Partial Gaps</h5>
              <div className="flex flex-wrap gap-1">
                {template.partialGaps.map((g) => (
                  <span key={g} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-orange-50 text-orange-700 border border-orange-200">
                    ⚠ {g}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Required fields */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Required Fields</h5>
            <div className="flex flex-wrap gap-1">
              {template.requiredFields.map((f) => (
                <span key={f} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                  {f}
                </span>
              ))}
            </div>
          </div>

          {/* Validation errors */}
          {template.validationResult.staticErrors.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-red-600 uppercase mb-1">Validation Errors</h5>
              <ul className="list-disc list-inside text-xs text-red-700 space-y-0.5">
                {template.validationResult.staticErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
